import { useEffect, useState } from 'react'
import { X, BellRing, Loader2, Check, AlertCircle } from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface ReminderDialogProps {
  isOpen: boolean
  onClose: () => void
  startDate: string
  monthsToShow: number
  cycles: Array<{ daysOn: number; daysOff: number }>
  patternLabel: string
  userEmail?: string
}

type Mode = 'block-start' | 'every-day'

export default function ReminderDialog({
  isOpen,
  onClose,
  startDate,
  monthsToShow,
  cycles,
  patternLabel,
  userEmail,
}: ReminderDialogProps) {
  const [mode, setMode] = useState<Mode>('block-start')
  const [leadDays, setLeadDays] = useState<number>(1)
  const [sendHour, setSendHour] = useState<number>(8)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ scheduled: number; email: string } | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setResult(null)
      setError('')
      setSending(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const scheduleReminders = async () => {
    setSending(true)
    setError('')
    setResult(null)
    try {
      const response = await lumi.functions.invoke('shiftReminders', {
        method: 'POST',
        body: {
          startDate,
          cycles,
          monthsToShow,
          patternLabel,
          mode,
          leadDays,
          sendHour,
          timezoneOffsetMinutes: new Date().getTimezoneOffset(),
        },
      })
      setResult({ scheduled: response?.scheduled ?? 0, email: response?.email ?? userEmail ?? '' })
    } catch (err: any) {
      console.error('Scheduling reminders failed:', err)
      setError(err?.data?.error || 'Could not schedule reminders. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const hours = Array.from({ length: 24 }, (_, h) => h)
  const formatHour = (h: number) => {
    const suffix = h < 12 ? 'AM' : 'PM'
    const display = h % 12 === 0 ? 12 : h % 12
    return `${display}:00 ${suffix}`
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reminder-dialog-title"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 id="reminder-dialog-title" className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-teal-600" />
            Email Shift Reminders
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
            aria-label="Close reminders dialog"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <p className="text-sm text-slate-600 mb-4">
          Get automatic emails before your shifts on the <span className="font-semibold">{patternLabel}</span>{' '}
          roster. Reminders go to {userEmail ? <span className="font-semibold">{userEmail}</span> : 'your account email'} and
          cover the next 60 days (up to 20 emails).
        </p>

        {result ? (
          <div className="mb-4 p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900 flex gap-2">
            <Check className="w-5 h-5 flex-shrink-0 text-green-600" />
            <p>
              {result.scheduled} reminder{result.scheduled === 1 ? '' : 's'} scheduled
              {result.email ? ` for ${result.email}` : ''}. They will arrive automatically.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <fieldset>
              <legend className="text-sm font-medium text-slate-700 mb-2">Remind me about</legend>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                  <input
                    type="radio"
                    name="reminder-mode"
                    checked={mode === 'block-start'}
                    onChange={() => setMode('block-start')}
                    className="accent-teal-600"
                  />
                  The first day of each work block
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                  <input
                    type="radio"
                    name="reminder-mode"
                    checked={mode === 'every-day'}
                    onChange={() => setMode('every-day')}
                    className="accent-teal-600"
                  />
                  Every work day
                </label>
              </div>
            </fieldset>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="reminder-lead" className="block text-sm font-medium text-slate-700 mb-1">
                  Send
                </label>
                <select
                  id="reminder-lead"
                  value={leadDays}
                  onChange={(e) => setLeadDays(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value={0}>On the day</option>
                  <option value={1}>1 day before</option>
                  <option value={2}>2 days before</option>
                  <option value={3}>3 days before</option>
                </select>
              </div>
              <div>
                <label htmlFor="reminder-hour" className="block text-sm font-medium text-slate-700 mb-1">
                  At (your time)
                </label>
                <select
                  id="reminder-hour"
                  value={sendHour}
                  onChange={(e) => setSendHour(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {hours.map((h) => (
                    <option key={h} value={h}>
                      {formatHour(h)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex gap-2" role="alert">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm"
          >
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={scheduleReminders}
              disabled={sending}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? 'Scheduling...' : 'Schedule reminders'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
